import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { Button, Modal } from "../components/ui";

const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

const DEFAULT_OPTIONS = {
  title: "Are you sure?",
  description: "This action cannot be undone.",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  danger: true,
};

// Drop-in, promise-based replacement for window.confirm() that matches the
// rest of the app's design system instead of popping the browser's raw
// native dialog. Usage stays just as simple as window.confirm() was:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: "Delete this post?", description: "..." }))) return;
//
export default function ConfirmProvider({ children }) {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setOptions({ ...DEFAULT_OPTIONS, ...opts });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (result) => {
    setOptions(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={Boolean(options)}
        onClose={() => handleClose(false)}
        title={options?.title}
        footer={
          <>
            <Button variant="secondary" onClick={() => handleClose(false)}>
              {options?.cancelLabel}
            </Button>
            <Button
              variant={options?.danger ? "danger" : "primary"}
              onClick={() => handleClose(true)}
              autoFocus
            >
              {options?.confirmLabel}
            </Button>
          </>
        }
      >
        {options && (
          <div className="flex items-start gap-3">
            {options.danger && (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                <FiAlertTriangle className="h-4 w-4" />
              </div>
            )}
            <p className="text-sm text-slate-600">{options.description}</p>
          </div>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}
